import { useEffect, useState, useRef } from 'react';
import styled from '@emotion/styled';
import { usePostMessage, useReceiveMessage } from '../../../context/messageContext';
import { useCalibration } from '../../../context/calibrationContext';
import { calibrationConstants } from '../../../helpers/calibration-constants';
import { useVideoRecording } from '../../../helpers/forth-valley-hooks';
import { useTestContext } from '../../../context/testContext';
import { Button, PanelTitle, PanelText, SessionActions, useSessionUI, colors, radii } from '../../../ui';
import { StepBody } from '../testScreenKit';

const PreviewBox = styled.div`
  position: relative;
  width: min(100%, 420px);
  aspect-ratio: 2;
  border: 2px solid ${colors.slate[500]};
  border-radius: ${radii.button}px;
  background: ${colors.slate[50]};
  align-self: center;
`;

// Stroke Suite step 1 - eye motility: the patient follows their video thumbnail to each corner
// while it records. Presentation only; recording, messages and timings are unchanged.

const EyeMotilityPhase = ({ updateStep }) => {
  const [currentVideoPosition, setCurrentVideoPosition] = useState({ top: '50%', left: '50%', centered: true });
  const [imagesReceived, setImagesReceived] = useState(false);
  const [videoReceived, setVideoReceived] = useState(false);
  const [transitionMovement, setTransitionMovement] = useState(true);
  const [canProgress, setCanProgress] = useState(false);
  const [loadingState, setLoadingState] = useState('idle');
  const postMessage = usePostMessage();
  const messageFromPatient = useReceiveMessage();
  const previewContainerRef = useRef();
  const { startRecording, stopRecording, videoRecorder } = useVideoRecording();
  const { blindSpotThumbnailWidthPx, blindSpotThumbnailHeightPx } = calibrationConstants;
  const { updateTestData } = useTestContext();
  const { confirm } = useSessionUI();
  const {
    calibrationData: { screenWidthPx, screenHeightPx },
  } = useCalibration();

  function* positionValueGenerator() {
    yield { top: 0, left: screenWidthPx - blindSpotThumbnailWidthPx };
    yield { top: 0, left: 0 };
    yield { top: screenHeightPx - blindSpotThumbnailHeightPx, left: screenWidthPx - blindSpotThumbnailWidthPx };
    yield { top: screenHeightPx - blindSpotThumbnailHeightPx, left: 0 };
  }

  const handleVideoMovement = async () => {
    const positionValue = positionValueGenerator();
    const numberOfPositions = 8;
    const transitionTime = 2000;
    const delayBeforeNextTransition = 1500;

    for (let pointInSequence = 0; pointInSequence <= numberOfPositions - 1; pointInSequence++) {
      if (videoRecorder?.state !== 'recording') {
        break;
      } else if (pointInSequence % 2) {
        setCurrentVideoPosition({ top: '50%', left: '50%', centered: true });
        if (pointInSequence === numberOfPositions - 1) {
          stopRecording();
          setLoadingState('loading');
        }
      } else setCurrentVideoPosition(positionValue.next().value);
      await new Promise((resolve) => {
        const delay = setTimeout(() => {
          return resolve();
        }, transitionTime + delayBeforeNextTransition);
        videoRecorder.addEventListener('stop', () => clearTimeout(delay));

        return videoRecorder.removeEventListener('stop', () => clearTimeout(delay));
      });
    }
  };

  useEffect(() => {
    if (loadingState !== 'loading') return;
    if (imagesReceived && videoReceived) return setLoadingState('complete');
    const loadingInterval = setInterval(() => {
      if (imagesReceived && videoReceived) return setLoadingState('complete');
    }, 1000);

    return () => clearInterval(loadingInterval);
  }, [loadingState, imagesReceived, videoReceived]);

  useEffect(() => {
    if (!transitionMovement) postMessage({ type: 'disableTransitionMovement' });
    if (transitionMovement) postMessage({ type: 'enableTransitionMovement' });
  }, [transitionMovement, postMessage]);

  useEffect(() => {
    if (videoRecorder?.state === 'recording') setCanProgress(false);
  }, [videoRecorder?.state]);

  useEffect(() => {
    if (imagesReceived && videoReceived) setCanProgress(true);
  }, [imagesReceived, videoReceived]);

  useEffect(() => {
    postMessage({ type: 'cornersFixation', content: currentVideoPosition });
  }, [currentVideoPosition, postMessage]);

  useEffect(() => {
    if (messageFromPatient.type === 'cornersFixationImgUrls') {
      localStorage.removeItem('cornerFixationImages');
      localStorage.setItem('cornerFixationImages', JSON.stringify(messageFromPatient.content));
      setImagesReceived(true);
    }

    if (messageFromPatient.type === 'videoRecordingData') {
      localStorage.removeItem('videoRecordingData');
      localStorage.setItem('videoRecordingData', JSON.stringify(messageFromPatient.content));
      setVideoReceived(true);
    }
  }, [messageFromPatient]);

  const repeatRecording = async () => {
    if (await confirm({ message: 'Repeat the eye motility stage?', detail: 'Previously captured images & video will be lost.', tone: 'danger' })) {
      setLoadingState('idle');
      return toggleVideoRecording();
    }
  };

  const toggleVideoRecording = () => {
    if (videoRecorder?.state === 'recording') {
      setCurrentVideoPosition({ top: '50%', left: '50%', centered: true });
      setTransitionMovement(false);
      stopRecording();
    } else {
      setImagesReceived(false);
      setVideoReceived(false);
      localStorage.removeItem('cornerFixationImages');
      localStorage.removeItem('videoRecordingData');
      setTransitionMovement(true);
      startRecording();
    }

    handleVideoMovement();
  };

  const interpolatePositionValuesForPreview = (values) => {
    let { top, left } = values;
    const { width, height } = previewContainerRef.current?.getBoundingClientRect() || {};

    if (top > height) {
      top = height - 30;
    } else if (!top) {
      top = 10;
    }

    if (left > width) {
      left = 10;
    } else if (!left) {
      left = width - 30;
    }

    return {
      ...values,
      transition: transitionMovement ? 'all 2s ease' : 'none',
      transform: currentVideoPosition.centered ? 'translate(-50%, -50%)' : null,
      top: top,
      left: left,
    };
  };

  const skipTest = async () => {
    if (await confirm({ message: 'Are you sure you want to skip the test and proceed straight to the video/image results?' })) return updateStep('results');
  };

  const recording = videoRecorder?.state === 'recording';
  const received = imagesReceived && videoReceived;

  return (
    <StepBody center gap={16} data-testid='neuro-motility'>
      <PanelTitle small>Patient Fixation Point Location</PanelTitle>
      <PreviewBox ref={previewContainerRef}>
        <div
          style={{
            position: 'absolute',
            width: 16,
            aspectRatio: '1',
            borderRadius: '50%',
            backgroundColor: colors.primary,
            ...interpolatePositionValuesForPreview(currentVideoPosition),
          }}
        />
      </PreviewBox>
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <Button
          size='lg'
          variant={recording ? 'destructive' : received ? 'secondary' : 'success'}
          onClick={() => {
            if (received) return repeatRecording();
            return toggleVideoRecording();
          }}
        >
          {recording ? 'Cancel' : received ? 'Repeat' : 'Begin'}
        </Button>
      </div>
      <PanelText small style={{ visibility: received || loadingState === 'loading' ? 'visible' : 'hidden', minHeight: 24 }}>
        {loadingState === 'loading'
          ? 'Processing images and video. Please wait...'
          : 'Images & video received from patient. Click "Next" to continue to the next part of the test, or "Skip Test" to view the images & video.'}
      </PanelText>
      <SessionActions onBack={() => updateTestData({ testKey: null })} backDisabled={recording} primary={{ label: 'Next', onClick: () => updateStep('familiarisationPhase'), disabled: !canProgress }}>
        <Button variant='secondary' size='xl' disabled={!canProgress} onClick={() => skipTest()}>
          Skip Test
        </Button>
      </SessionActions>
    </StepBody>
  );
};

export default EyeMotilityPhase;
